import clsx from 'clsx';
import ReactTable, { Column, RowInfo, SortingRule } from 'react-table';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { CustomPagination } from './Pagination';
import { Pagination } from 'global';
import { usePagination, useFilteredData } from './hooks';

interface Props<Entity> {
  loading?: boolean;
  tableData: Entity[];
  columns: Column<Entity>[];
  paginationOptions?: Partial<Omit<Pagination, 'total'>>;
  onRowClick?: (row: Entity) => void;
  defaultSorted?: SortingRule[];
}

export const Table = <Entity,>({
  tableData,
  columns,
  paginationOptions,
  onRowClick,
  loading = false,
  defaultSorted = [],
}: Props<Entity>) => {
  const classes = useStyles();

  const paginationProps = usePagination(tableData.length, paginationOptions);
  const { filteredData, onFilteredChange } = useFilteredData<Entity>(paginationProps, tableData);

  return (
    <ReactTable<Entity>
      loading={loading}
      columns={columns}
      showPaginationBottom
      PaginationComponent={CustomPagination}
      defaultSorted={defaultSorted}
      data={loading ? [] : filteredData}
      className={clsx('-highlight', classes.table)}
      onFilteredChange={onFilteredChange}
      loadingText={
        <Typography component="span" variant="h6">
          Data loading...
        </Typography>
      }
      noDataText={
        !loading && (
          <Typography component="span" variant="h6">
            No data
          </Typography>
        )
      }
      getTdProps={(state: any, rowInfo: RowInfo | undefined, column: Column | undefined, instance: any) => {
        return {
          onClick: (e: any, handleOriginal: () => void) => {
            if (onRowClick) {
              onRowClick(rowInfo?.original);
            }
            // IMPORTANT! React-Table uses onClick internally to trigger
            // events like expanding SubComponents and pivots.
            // By default a custom 'onClick' handler will override this functionality.
            // If you want to fire the original onClick handler, call the
            // 'handleOriginal' function.
            if (handleOriginal) {
              handleOriginal();
            }
          },
          style: {
            cursor: onRowClick ? 'pointer' : 'inherit',
          },
        };
      }}
      {...paginationProps}
    />
  );
};

const useStyles = makeStyles({
  table: {
    width: '100%',
    '& .-loading > div': {
      color: 'black',
    },

    '& .rt-noData': {
      top: '50%',
      left: '50%',
      color: 'black',
      transform: 'translate(-50%, -50%)',
    },

    '& .customHeader': {
      margin: 0,
    },

    '& .customHeader > div': {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '18px',
      fontFamily: 'Roboto',
    },

    '& .customHeader > div::after': {
      color: 'black',
      fontWeight: 500,
    },

    '& .rt-thead.-filters input': {
      backgroundImage: 'none',
    },

    '& .rt-thead.-filters input:focus': {
      backgroundImage: 'none',
    },
  },
});
